import { createAsyncThunk } from '@reduxjs/toolkit';

import type { AddToCartRequest } from '../../cart/models/cart.model';
import { CartAction } from '../../cart/models/cart.model';
import { parseShopifyCartError } from '../../cart/utils/cart.utils';
import { ApiError } from '../../models/api-error.enum';
import { shopifyClient } from '../../shopify/shopify.client';
import { shopifyService } from '../../shopify/shopify.service';
import type { RootState } from '../../store';
import type {
  Contract,
  Membership,
  MembershipContractAddressUpdateRequest,
  MembershipContractUpdateCancellationRequest,
  MembershipContractUpdateRequest,
  ReferralLink,
  TermProductVariant,
  WalletType,
} from '../models/membership.model';
import { membershipService } from '../services/membership.service';
import { isNonRenewingTerm } from '../utils/membership.formatters';
import { getTermForProductVariant } from '../utils/membership.utlis';

const cartService = shopifyService.cartService;

export const loadMemberships = createAsyncThunk<
  Membership[],
  void,
  {
    rejectValue: ApiError;
  }
>('membership/loadMemberships', async (_, { rejectWithValue }) => {
  try {
    const memberships = await membershipService.getManageData();
    return await membershipService.getMembershipProducts(memberships);
  } catch (error: any) {
    return rejectWithValue(error.message ?? 'Failed to load memberships');
  }
});

export const loadAddressData = createAsyncThunk<
  string,
  void,
  {
    rejectValue: ApiError;
  }
>('membership/loadAddressData', async (_, { rejectWithValue }) => {
  try {
    return await membershipService.getAddressData();
  } catch (error: any) {
    return rejectWithValue(error.message ?? 'Failed to load address data');
  }
});

export const updateMembership = createAsyncThunk<
  void,
  MembershipContractUpdateRequest,
  {
    rejectValue: ApiError;
    state: RootState;
  }
>(
  'membership/updateMembership',
  async (request: MembershipContractUpdateRequest, { dispatch, rejectWithValue, getState }) => {
    try {
      await membershipService.updateMembership(request);

      // Poll for updated contract data
      const maxAttempts = 10;
      const pollInterval = 500; // 500ms between attempts
      let attempts = 0;

      while (attempts < maxAttempts) {
        attempts++;

        // Reload memberships
        await dispatch(loadMemberships()).unwrap();

        // Check if contract has been updated
        const state = getState();
        const memberships = state.membership.memberships.value;
        const membership = memberships?.find((m) => m.id === request.fanSubscriptionId);

        if (membership?.contract?.tier?.id === request.tierId) {
          // Contract has been updated successfully
          console.log(`[updateMembership] Contract updated after ${attempts} attempts`);
          return;
        }

        // Wait before next attempt
        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }
      }

      // If we get here, polling timed out but the update likely succeeded
      console.warn('[updateMembership] Polling timed out, but update may have succeeded');
      return;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to update membership');
    }
  }
);

export const cancelMembershipUpdate = createAsyncThunk<
  void,
  MembershipContractUpdateCancellationRequest,
  {
    rejectValue: ApiError;
  }
>(
  'membership/cancelMembershipUpdate',
  async (request: MembershipContractUpdateCancellationRequest, { dispatch, rejectWithValue }) => {
    try {
      await membershipService.cancelMembershipUpdate(request);
      // Reload memberships after successful cancel
      dispatch(loadMemberships());
      return;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to cancel membership update');
    }
  }
);

export const updateMembershipAddress = createAsyncThunk<
  void,
  MembershipContractAddressUpdateRequest,
  {
    rejectValue: ApiError;
  }
>(
  'membership/updateMembershipAddress',
  async (request: MembershipContractAddressUpdateRequest, { dispatch, rejectWithValue }) => {
    try {
      await membershipService.updateAddress(request);
      // Reload memberships after successful update
      dispatch(loadMemberships());
      return;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to update address');
    }
  }
);

export const updatePaymentMethod = createAsyncThunk<
  void,
  Contract,
  {
    rejectValue: ApiError;
  }
>('membership/updatePaymentMethod', async (contract: Contract, { rejectWithValue }) => {
  try {
    await membershipService.updatePaymentMethod(contract);
    return;
  } catch (error: any) {
    return rejectWithValue(error.message ?? 'Failed to request payment update email');
  }
});

export const createContract = createAsyncThunk<
  {
    redirectUrl?: string;
  },
  {
    tierId: string;
    termId: string;
    redirectUrl?: string;
    referralLinkId?: string;
    optInToMarketingEmails?: boolean;
  },
  {
    rejectValue: ApiError;
  }
>(
  'membership/createContract',
  async (
    params: {
      tierId: string;
      termId: string;
      redirectUrl?: string;
      referralLinkId?: string;
      optInToMarketingEmails?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      // Get customer info first
      const customer = await membershipService.getCustomerInfo();
      if (!customer?.id) {
        return rejectWithValue(ApiError.Session_Not_Ready);
      }
      localStorage.setItem('single_customer_id', customer.id);
      localStorage.setItem('single_customer_email', customer.email);

      // Create contract
      await membershipService.createContract(
        params.tierId,
        params.termId,
        params.referralLinkId,
        params.optInToMarketingEmails
      );

      return { redirectUrl: params.redirectUrl };
    } catch (error: any) {
      // 409 conflict means contract already exists - treat as success
      if (error.message?.includes('409')) {
        return { redirectUrl: params.redirectUrl };
      }
      return rejectWithValue(error.message ?? 'Failed to create contract');
    }
  }
);

export const addMembershipToCart = createAsyncThunk<
  { success: true },
  {
    request: TermProductVariant;
    tierId: string;
    referralLinkId?: string;
    optInToMarketingEmails?: boolean;
  },
  {
    rejectValue: {
      error: ApiError;
      message?: string;
    };
    state: RootState; // your root state type
  }
>('membership/addMembershipToCart', async (params, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const memberships = state.membership.memberships.value;

    // Get product from Shopify
    const product = await shopifyClient.getProduct(params.request.handle, true);

    if (!product || !Array.isArray(product.variants)) {
      throw new Error('Invalid product structure');
    }

    // Find selling plan ID. OTP (non-renewing) tiers have no selling plan;
    // recurring tiers must have one.
    const variantId = Number(params.request.sourceVariantId);
    const variant = product.variants.find((v) => v.id === variantId);
    const sellingPlanId = variant?.selling_plan_allocations[0]?.selling_plan_id;

    const term = getTermForProductVariant(
      memberships ?? [],
      product.id?.toString(),
      String(params.request.sourceVariantId)
    );
    const isOneTimePurchase = isNonRenewingTerm(term);

    if (!sellingPlanId && !isOneTimePurchase) {
      throw new Error('Selling plan not found for variant');
    }

    await cartService.addToCart(
      {
        originalAddToCartButton: null,
        addToCartButton: null,
        productVariantId: params.request.sourceVariantId,
        upsell: false,
        membership: true,
        cartAction: CartAction.NONE,
        sellingPlanId: isOneTimePurchase ? undefined : sellingPlanId?.toString(),
        referralLinkId: params.referralLinkId,
        optInToMarketingEmails: params.optInToMarketingEmails,
      } as AddToCartRequest,
      memberships,
      product
    );

    return { success: true };
  } catch (error: any) {
    return rejectWithValue({
      error: ApiError.Other,
      message: parseShopifyCartError(error),
    });
  }
});

export const loadReferralLink = createAsyncThunk<
  ReferralLink,
  string,
  {
    rejectValue: ApiError;
  }
>('membership/loadReferralLink', async (id: string, { rejectWithValue }) => {
  try {
    return await membershipService.getReferralLink(id);
  } catch (error: any) {
    return rejectWithValue(error.message ?? 'Failed to load referral link');
  }
});

export const downloadWalletPass = createAsyncThunk<
  string,
  {
    contract: Contract;
    walletType: WalletType;
  },
  {
    rejectValue: ApiError;
  }
>(
  'membership/downloadWalletPass',
  async (
    params: {
      contract: Contract;
      walletType: WalletType;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await membershipService.getWalletPassUrl(params.contract, params.walletType);
      return response.url;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to get wallet pass URL');
    }
  }
);
